import { useStore } from '@/hooks/useStore';
import { isPathEk } from '@/utils/routes';
import { formatCurrency } from '@/utils/formatNumber';
import { observer } from 'mobx-react-lite';
import { ReactNode, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Form, FormControl, FormField, FormItem } from '@/components/common/Form';
import yup from '@/services/yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Combobox from '@/components/common/Combobox';
import { ADDITIONAL_CHARGE_TYPE, COMMON_TYPE, DROPOFF_OPTION, GROUP_CAPACITY_TYPE, INSURENCE_OPTION, OPTION_FORMAT, OTHER_SETTING_TYPE } from '@/types/enums';
import { flowResult } from 'mobx';
import { getFromSession, saveToSession } from '@/utils/browsers';
import MapWithSearch from '../components/map/MapWithSearch';
import RuleKilometerLimit from '../components/modal/RuleKilometerLimit';
import { ekDomain, MILEAGE_LIMIT_GROUP, ROUTES } from '@/configs/constants';
import StepWrapper from '../components/step-container/StepWrapper';
import OptionSelectItem from '../components/options/OptionSelectItem';
import OptionComponent from '../components/options/OptionComponent';
import AnimateButton from '@/components/general/button-custom/AnimateButton';
import { OptionOtherSetting } from '@/types/option';

export default observer(function StepSecondBookingPage() {
    // hook
    const { t } = useTranslation();
    const { id, bikeId } = useParams();
    const [searchParams] = useSearchParams();
    const uuid = searchParams.get('uuid');
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const isEk = isPathEk(pathname);

    const type = isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS;

    const handleDefaultRedirect = () => {
        window.location.href = (
            isEk ?
                `/${ekDomain}/${ROUTES.locationList.href}/detail/${id}/bike-booking-first/${bikeId}` :
                `/${ROUTES.locationList.href}/detail/${id}/bike-booking-first/${bikeId}`
        );
    };

    if (!uuid) {
        handleDefaultRedirect();
    }

    const currentData = getFromSession(uuid || '');

    if (!currentData || !currentData.bikeId || !currentData.locationId || (currentData.locationId && currentData.locationId !== id) || (currentData.bikeId && currentData.bikeId !== bikeId)) {
        handleDefaultRedirect();
    }

    // store
    const {
        bookingStore: {
            getBikeBookingDetail, bikeBookingDetail,
            options, getBookingOption, postCheckingBookingData
        },
        generalStore: {
            otherSetting,
            getOtherSetting
        },
        modalStore: {
            showModal
        }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        additionalServices: yup.array().nullable()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            additionalServices: []
        }
    });

    const watchAdditionalService = form.watch('additionalServices');

    // lifecycle
    useEffect(() => {
        getBikeBookingDetail(Number(bikeId), Number(id), type);
        getDataOptional();
        if (!isEk) {
            getOtherSetting();
        }
    }, []);

    // function

    const [optionList, compensationList] = useMemo(() => {
        const { option = [], compensation = [] } = options;
        // console.log(option, compensation);
        return [option, compensation];
    }, [options]);

    const [dropOffList, extraMileageList] = useMemo(() => {
        let dropOffList: OptionOtherSetting[] = [];
        let extraMileageList: OptionOtherSetting[] = [];
        const { dropOff, mileageLimit } = otherSetting;

        if (dropOff && dropOff.length > 0) {
            dropOffList = dropOff.map((item) => {
                return ({
                    name: `${formatCurrency(item.price)}${t('currency.currency_unit')}/1km\n貸出拠点起算`,
                    id: Number(item?.id || 0),
                    price: Number(item.price),
                    format: OPTION_FORMAT.DropOff,
                    status: 1
                });
            });
        }

        if (mileageLimit && Object.keys(mileageLimit).length > 0) {
            const { variable } = mileageLimit;
            let bikeCapacityVal = bikeBookingDetail?.bikeCapacity?.id;
            extraMileageList = Object.keys(variable).filter(key => bikeCapacityVal ? (Number(bikeCapacityVal) <= 2 ? MILEAGE_LIMIT_GROUP[key].value === GROUP_CAPACITY_TYPE.LQ_125CC : MILEAGE_LIMIT_GROUP[key].value === GROUP_CAPACITY_TYPE.GQ_126CC) : key).map((key) => {
                return ({
                    id: variable[key][0].id,
                    // name: `${t('price_list.title_section.mileage_limit')}:${MILEAGE_LIMIT_GROUP[key].label}`,
                    name: MILEAGE_LIMIT_GROUP[key].label,
                    label: `${t('price_list.title_section.mileage_limit')}: ${MILEAGE_LIMIT_GROUP[key].label}`,
                    price: variable[key][0].price,
                    maxValue: 10,
                    status: 1,
                    format: OPTION_FORMAT.ExtraMileage,
                    mileageLimit: `/${MILEAGE_LIMIT_GROUP[key].km}km`,
                    capacityType: MILEAGE_LIMIT_GROUP[key].value,
                    formatSetting: OTHER_SETTING_TYPE.MILEAGE_LIMIT,
                    additionalChargesType: ADDITIONAL_CHARGE_TYPE.VARIABLE
                });
            });
        }

        return [dropOffList, extraMileageList];
    }, [otherSetting, bikeBookingDetail]);

    const getDataOptional = async () => {
        const res = await flowResult(getBookingOption());
        if (res) {
            if (currentData?.additionalServices && currentData.additionalServices?.length > 0) {
                let addSrv = [] as any;
                currentData.additionalServices.forEach((service) => {
                    if (res) {
                        const { option = [], compensation = [] } = res;
                        const findOptionIndex = option.findIndex(option => String(option.id) === String(service.id) && Number(service.format) === OPTION_FORMAT.Option);
                        if (findOptionIndex > -1) {
                            addSrv.push({
                                ...service,
                                price: option[findOptionIndex].price
                            });
                        }

                        const findCompensationIndex = compensation.findIndex(option => String(option.id) === String(service.id) && Number(service.format) === OPTION_FORMAT.Insurance);

                        if (findCompensationIndex > -1) {
                            addSrv.push({
                                ...service,
                                price: compensation[findCompensationIndex].price
                            });
                        }

                        if ([OPTION_FORMAT.DropOff.valueOf(), OPTION_FORMAT.ExtraMileage.valueOf()].includes(Number(service?.format))) {
                            addSrv.push({
                                ...service
                            });
                        }

                        form.setValue('additionalServices', addSrv);
                    }
                });
            }
        }
    };

    const handleChangeQuantity = (value: string | number, id: number | string, price: number | string | null = 0) => {
        if (watchAdditionalService && watchAdditionalService.length > 0) {
            const findOption = watchAdditionalService?.findIndex(item => (String(item.id) === String(id) && Number(item.format) === OPTION_FORMAT.Option));

            if (findOption > -1) {
                let cloneAdditionalSrv = [...watchAdditionalService];
                if (Number(value) > 0) {
                    cloneAdditionalSrv[findOption].value = Number(value);
                } else {
                    cloneAdditionalSrv = cloneAdditionalSrv.filter((_, indx) => indx !== findOption);
                }
                form.setValue('additionalServices', [...cloneAdditionalSrv]);
            } else {
                Number(value) > 0 && form.setValue('additionalServices', [...watchAdditionalService, { id: id, value: Number(value), price: price, format: OPTION_FORMAT.Option }]);
            }
        } else {
            Number(value) > 0 && form.setValue('additionalServices', [{ id: id, value: Number(value), price: price, format: OPTION_FORMAT.Option }]);
        }
    };

    const handleChangeQuantityExtraMileage = (value: string | number, selectedItem: OptionOtherSetting) => {
        const { id, price, label: name, formatSetting, capacityType, additionalChargesType } = selectedItem;
        let service = {
            id: id, value: Number(value), price: price, format: OPTION_FORMAT.ExtraMileage, name, formatSetting, capacityType, additionalChargesType
        };
        if (watchAdditionalService && watchAdditionalService.length > 0) {
            const findOption = watchAdditionalService?.findIndex(item => String(item.id) === String(id) && Number(item.format) === OPTION_FORMAT.ExtraMileage);
            if (findOption > -1) {
                let cloneAdditionalSrv = [...watchAdditionalService];
                if (Number(value) > 0) {
                    cloneAdditionalSrv[findOption].value = Number(value);
                } else {
                    cloneAdditionalSrv = cloneAdditionalSrv.filter((_, indx) => indx !== findOption);
                }
                form.setValue('additionalServices', [...cloneAdditionalSrv]);
            } else {
                Number(value) > 0 && form.setValue('additionalServices', [...watchAdditionalService, { ...service }]);
            }
        } else {
            Number(value) > 0 && form.setValue('additionalServices', [{ ...service }]);
        }
    };

    const onBack = () => {
        navigate(
            isEk ?
                `/${ekDomain}/${ROUTES.locationList.href}/detail/${id}/bike-booking-first/${bikeId}?uuid=${uuid}` :
                `/${ROUTES.locationList.href}/detail/${id}/bike-booking-first/${bikeId}?uuid=${uuid}`
        );
    };

    const onSubmit = async (data: FormData) => {
        let additionalServices = data.additionalServices?.map((item) => {
            let value: any = '';
            if (item.value !== '') {
                value = Number(item.value);
            }

            return {
                ...item,
                value
            };
        }) || [];
        const currentData = getFromSession(uuid || '');
        const payload = {
            ...currentData,
            additionalServices
        };

        const res = await flowResult(postCheckingBookingData(payload));
        if (res) {
            uuid && saveToSession(uuid, {
                ...payload,
                dateAndTimeCost: res.dateAndTimeCost || 0,
                dateAndTime: res.dateAndTime
            });
            navigate(
                isEk ?
                    `/${ekDomain}/${ROUTES.locationList.href}/detail/${id}/bike-booking-third/${bikeId}?uuid=${uuid}` :
                    `/${ROUTES.locationList.href}/detail/${id}/bike-booking-third/${bikeId}?uuid=${uuid}`
            );
        }
    };

    const showKilometerLimit = () => {
        showModal({
            id: 'kilometer-limit',
            title: '',
            size: '6xl',
            titleClassName: 'text-center max-w-full',
            contentClassName: 'max-w-full overflow-hidden',
            content: (
                <RuleKilometerLimit />
            ),
            footerClassName: 'hidden'
        });
    };

    return (
        <div>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className='relative'>
                    <StepWrapper currentStep={2}>
                        <div className='flex flex-col md:gap-20 md:md-20 xsm:gap-[60px] xsm:mt-[60px] gap-[30px] mt-[30px]'>
                            {/* Option */}
                            <FormField
                                control={form.control}
                                name='additionalServices'
                                render={() => (
                                    <FormItem className=''>
                                        <OptionComponent
                                            option={{
                                                type: OPTION_FORMAT.Option,
                                                title: t('price_list.title_section.option'),
                                                textDecoration: t('price_list.text_decoration.option'),
                                                inputChildren: (
                                                    <>
                                                        {optionList?.length > 0 && optionList.map((item, index) => {
                                                            return (
                                                                <FormField
                                                                    key={item?.name + index}
                                                                    control={form.control}
                                                                    name='additionalServices'
                                                                    render={() => (
                                                                        <FormItem className='flex gap-2'>
                                                                            <FormControl>
                                                                                <OptionSelectItem
                                                                                    option={item}
                                                                                    type={OPTION_FORMAT.Option}
                                                                                    onChangeValueFn={(value) => {
                                                                                        handleChangeQuantity(value, item.id, item.price);
                                                                                    }}
                                                                                    selectedValue={watchAdditionalService?.find(srv => (String(srv.id) === String(item.id) && Number(srv.format) === OPTION_FORMAT.Option))?.value || 0}
                                                                                />
                                                                            </FormControl>

                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            );
                                                        })}
                                                    </>)
                                            }}
                                        />
                                    </FormItem>
                                )}
                            />
                            {
                                !isEk && (
                                    <>
                                        {/* Compensation */}
                                        <FormField
                                            control={form.control}
                                            name='additionalServices'
                                            render={({ field }) => {
                                                // Tìm phần tử đã chọn thuộc compensationList (nếu có)
                                                const currentCompensation = field.value?.find(service =>
                                                    compensationList?.some(item => (item.id === service.id && service?.format === OPTION_FORMAT.Insurance)));

                                                return (
                                                    <FormItem className=''>
                                                        <OptionComponent
                                                            option={{
                                                                type: OPTION_FORMAT.Insurance,
                                                                title: '車両補償オプション料金',
                                                                textDecoration: '車両補償オプション',
                                                                descriptionList: [
                                                                    '立ちゴケ安心制度の補償範囲を超えてバイク修理が必要となった場合のための補償です。',
                                                                    '車両補償オプションを選んでおくことで、自己負担金額を抑えることが出来ます。',
                                                                    <>
                                                                        レッカーなどの付帯サービスについて詳しくは
                                                                        <Link
                                                                            target='_blank'
                                                                            to={`${isEk ? ekDomain : ''}/${ROUTES.priceList.href}#towing-service`}
                                                                            className='w-fit cursor-pointer text-primaryWeb underline'
                                                                        >
                                                                            こちら
                                                                        </Link>
                                                                        をご覧下さい。
                                                                    </>
                                                                ],
                                                                descriptionCompList: [
                                                                    <Link
                                                                        target='_blank'
                                                                        className='cursor-pointer underline text-primaryWeb'
                                                                        key={compensationList?.length}
                                                                        to={`${isEk ? ekDomain : ''}/${ROUTES.priceList.href}#towing-service`}
                                                                    >
                                                                        &gt;&gt;詳しくはこちら
                                                                    </Link>
                                                                ],
                                                                specialNote: [
                                                                    '※通常の車両補償は免責5万円、最大30万円まで補償',
                                                                    '※Premiumクラスは免責5万円、最大80万円まで補償',
                                                                    '※ 最大30日までの休業補償が無料で付帯いたします'
                                                                ],
                                                                inputChildren: (
                                                                    <>
                                                                        {compensationList?.length > 0 && compensationList.map((item, index) => {
                                                                            const currentOptionValue = currentCompensation?.id === item.id && item.format === OPTION_FORMAT.Insurance ? INSURENCE_OPTION.SUBSCRIBE : INSURENCE_OPTION.NOT_SUBSCRIBE;

                                                                            return (
                                                                                <FormField
                                                                                    key={index}
                                                                                    control={form.control}
                                                                                    name='additionalServices'
                                                                                    render={({ field }) => (
                                                                                        <FormItem className='flex gap-2'>
                                                                                            <FormControl>
                                                                                                <OptionSelectItem
                                                                                                    option={item}
                                                                                                    type={OPTION_FORMAT.Insurance}
                                                                                                    isCheckOffOption
                                                                                                    onChangeValueFn={(value) => {
                                                                                                        if (value === INSURENCE_OPTION.SUBSCRIBE.toString()) {
                                                                                                            // Lọc bỏ tất cả các object thuộc compensationList trong additionalServices
                                                                                                            const otherServices = field.value?.filter(service => service?.format !== OPTION_FORMAT.Insurance) || [];
                                                                                                            // Thêm object được chọn vào additionalServices
                                                                                                            field.onChange([
                                                                                                                ...otherServices,
                                                                                                                { id: item.id, value: null, price: item.price, format: OPTION_FORMAT.Insurance }
                                                                                                            ]);
                                                                                                        } else {
                                                                                                            if (currentCompensation?.id.toString() === item.id.toString()) {
                                                                                                                field.onChange(field.value?.filter(service => service?.format !== OPTION_FORMAT.Insurance) || []);
                                                                                                            }
                                                                                                        }
                                                                                                    }}
                                                                                                    selectedValue={String(currentOptionValue)}
                                                                                                />
                                                                                            </FormControl>

                                                                                        </FormItem>
                                                                                    )}
                                                                                />
                                                                            );
                                                                        })}
                                                                    </>)
                                                            }}
                                                        />
                                                    </FormItem>
                                                );
                                            }}
                                        />
                                        {/* Extra Mileage */}
                                        <FormField
                                            control={form.control}
                                            name='additionalServices'
                                            render={() => (
                                                <FormItem className=''>
                                                    <OptionComponent
                                                        option={{
                                                            type: OPTION_FORMAT.ExtraMileage,
                                                            title: '長距離走行オプション料金',
                                                            textDecoration: '長距離走行オプション',
                                                            descriptionList: [
                                                                '基準となる走行距離を超えて走行する場合、下記から距離を計算の上オプションを選択して下さい。',
                                                                <>
                                                                    距離オプションの計算方法について、詳しくは
                                                                    <span
                                                                        key='link'
                                                                        onClick={showKilometerLimit}
                                                                        className='w-fit cursor-pointer text-primaryWeb underline'
                                                                    >
                                                                        こちら
                                                                    </span>
                                                                    をご覧下さい。
                                                                </>
                                                            ],
                                                            descriptionCompList: [
                                                                <div key='link' onClick={showKilometerLimit} className='w-fit cursor-pointer text-primaryWeb underline'>&gt;&gt;詳しくはこちら</div>
                                                            ],
                                                            otherComponent: (
                                                                <MapWithSearch className='mb-5' showDescription={showKilometerLimit} />
                                                            ),
                                                            isPrice: false,
                                                            inputChildren: (
                                                                <>
                                                                    {extraMileageList?.length > 0 && extraMileageList.map((item, index) => {
                                                                        return (
                                                                            <FormField
                                                                                key={index}
                                                                                control={form.control}
                                                                                name='additionalServices'
                                                                                render={() => (
                                                                                    <FormItem className='flex gap-2'>
                                                                                        <FormControl>
                                                                                            <OptionSelectItem
                                                                                                useSearch={false}
                                                                                                option={item}
                                                                                                type={OPTION_FORMAT.ExtraMileage}
                                                                                                onChangeValueFn={(value) => {
                                                                                                    handleChangeQuantityExtraMileage(value, item);
                                                                                                }}
                                                                                                selectedValue={watchAdditionalService?.find(srv => (String(srv.id) === String(item.id) && Number(srv.format) === OPTION_FORMAT.ExtraMileage))?.value || 0}
                                                                                            />
                                                                                        </FormControl>
                                                                                    </FormItem>
                                                                                )}
                                                                            />
                                                                        );
                                                                    })}
                                                                </>)
                                                        }}
                                                    />
                                                </FormItem>
                                            )}
                                        />

                                        {/* Drop-off */}
                                        <FormField
                                            control={form.control}
                                            name='additionalServices'
                                            render={() => (
                                                <FormItem className=''>
                                                    <OptionComponent
                                                        option={{
                                                            type: OPTION_FORMAT.DropOff,
                                                            title: '乗り捨てオプション料金',
                                                            textDecoration: '乗り捨てオプション',
                                                            descriptionList: ['乗り捨てオプションにつきましては、ご予約完了の後に別途メールにて乗り捨て拠点を確認させて頂き、正確な金額を算出させて頂きます。'],
                                                            isPrice: false,
                                                            inputChildren: (
                                                                <>
                                                                    {dropOffList?.length > 0 && dropOffList.map((item, index) => {
                                                                        const currentOptionValue = watchAdditionalService?.find(option => (String(option?.id) === String(item?.id) && option.format === OPTION_FORMAT.DropOff)) ? DROPOFF_OPTION.ADD : DROPOFF_OPTION.NOT_ADD;

                                                                        return (
                                                                            <FormField
                                                                                key={index}
                                                                                control={form.control}
                                                                                name='additionalServices'
                                                                                render={({ field }) => (
                                                                                    <FormItem className='flex gap-2'>
                                                                                        <FormControl>
                                                                                            <OptionSelectItem
                                                                                                option={item}
                                                                                                optionNameClass='font-normal xsm:justify-end justify-center bg-white'
                                                                                                type={OPTION_FORMAT.DropOff}
                                                                                                isCheckOffOption
                                                                                                comboboxClass='xsm:w-32 w-[120px]'
                                                                                                onChangeValueFn={(value) => {
                                                                                                    if (value === DROPOFF_OPTION.ADD.toString()) {
                                                                                                        field.onChange([
                                                                                                            ...field.value || [],
                                                                                                            {
                                                                                                                id: item.id, value: null,
                                                                                                                price: item.price,
                                                                                                                format: OPTION_FORMAT.DropOff,
                                                                                                                name: t('price_list.title_section.drop_off'),
                                                                                                                formatSetting: OTHER_SETTING_TYPE.DROP_OFF,
                                                                                                                capacityType: GROUP_CAPACITY_TYPE.NONE,
                                                                                                                additionalChargesType: ADDITIONAL_CHARGE_TYPE.NONE
                                                                                                            }
                                                                                                        ]);
                                                                                                    } else {
                                                                                                        field.onChange(field.value?.filter(service => service?.format !== OPTION_FORMAT.DropOff) || []);
                                                                                                    }
                                                                                                }}
                                                                                                selectedValue={currentOptionValue.toString()}
                                                                                            />
                                                                                        </FormControl>

                                                                                    </FormItem>
                                                                                )}
                                                                            />
                                                                        );
                                                                    })}
                                                                </>)
                                                        }}
                                                    />
                                                </FormItem>
                                            )}
                                        />
                                    </>
                                )
                            }

                            <div className='flex sm:flex-row flex-col justify-center lg:gap-[80px] md:gap-[60px] gap-[30px] items-center'>
                                <AnimateButton
                                    type='button'
                                    side='left'
                                    parentClassName='md:w-[420px] xsm::w-80 w-full sm:order-1 order-2'
                                    className=' min-h-[67px]'
                                    contentComp={t('button.back')}
                                    outline
                                    onClick={onBack}
                                >
                                </AnimateButton>
                                <AnimateButton
                                    type='submit'
                                    parentClassName='md:w-[420px] xsm::w-80 w-full sm:order-2 order-1'
                                    className=' min-h-[67px]'
                                    contentComp={t('button.next')}
                                >
                                </AnimateButton>
                            </div>
                        </div>
                    </StepWrapper>

                </form>
            </Form>

        </div>
    );
});

interface OptionItemProps {
    name: ReactNode,
    price: number,
    quantity: string,
    maxValue?: number,
    onChange: Function,
    disabled?: boolean
}
export function OptionItem({ name, price, quantity, maxValue, onChange, disabled }: OptionItemProps) {
    // hook
    const { t } = useTranslation();

    const quantityOptionList = Array.from({ length: maxValue ? (maxValue + 1) : 2 }).map((_, number) => ({
        label: (number).toString(),
        value: (number).toString()
    }));

    return (
        <div className='flex flex-col w-full gap-2'>
            <label className='font-semibold'>{name}</label>
            <div className='flex gap-4 items-center'>
                <label className='text-text-3 !w-20 font-medium'>
                    {t('booking_bike.field.quanity')}
                    :
                </label>
                <div className='min-w-32'>
                    <Combobox
                        search={quantityOptionList.length >= 10}
                        items={quantityOptionList}
                        value={quantity.toString()}
                        onChange={(item) => {
                            onChange(item.value.toString());
                        }}
                        className='min-w-32'
                        disabled={disabled}
                        disableClearIcon
                    />
                </div>
            </div>
            <div className='flex gap-4 items-center'>
                <label className='text-text-3 w-20 font-medium'>
                    {t('booking_bike.field.price')}
                    :
                </label>
                <p className='font-semibold'>
                    {t('currency.currency_symbol')}
                    {formatCurrency(price)}
                </p>
            </div>
        </div>
    );
}
